import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { ArrowRight, Coins, Search, Sparkles, BadgePercent, ListChecks } from "lucide-react";
import { api } from "../lib/api";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/49dc7a32-584f-4bdf-87ef-ecfe60004508/images/13e48711fd7959703a5e5c6f25853e129dc20ed60294acdccec0ac00aa6dd8d6.png";
const LOGO_URL = "/ab-microplay-logo.png";

const TILES = [
  {
    to: "/quote",
    eyebrow: "01",
    title: "Trade-In / Cash Out Quote",
    desc: "Get an instant quote for the games you want to sell or trade. Add as many items as you'd like.",
    icon: Coins,
    testid: "tile-quote",
  },
  {
    to: "/inventory-lookup",
    eyebrow: "02",
    title: "Shop Inventory",
    desc: "Browse what we have in stock by console, reserve items, and we'll set a pickup window.",
    icon: Search,
    testid: "tile-lookup",
  },
  {
    to: "/account-lookup",
    eyebrow: "03",
    title: "AB Member — Account Lookup",
    desc: "Check your µPoints balance and redemption tiers. Not a member yet? Sign up free in-store.",
    icon: BadgePercent,
    testid: "tile-account",
  },
  {
    to: "/wait-list",
    eyebrow: "04",
    title: "Wait List a Game",
    desc: "Looking for something we don't have yet? Tell us — we'll email you the moment it's back in stock.",
    icon: ListChecks,
    testid: "tile-waitlist",
  },
];

export default function Landing() {
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(true);

  useEffect(() => {
    api.get("/promotion/current")
      .then((r) => setPromo(r.data))
      .catch(() => setPromo(null))
      .finally(() => setPromoLoading(false));
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.85) 60%, rgba(10,10,10,1) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-28 sm:pt-28 sm:pb-36">
          <img
            src={LOGO_URL}
            alt="AB Microplay"
            className="w-full max-w-[520px] sm:max-w-[640px] h-auto -ml-2 sm:-ml-4 drop-shadow-[0_0_40px_rgba(59,130,246,0.35)]"
            data-testid="landing-logo"
          />
          <div className="mt-4 flex items-center gap-3">
            <span
              className="inline-block w-2 h-2"
              style={{ background: "var(--ab-accent-red)", boxShadow: "0 0 10px var(--ab-accent-red)" }}
            />
            <span className="label-eyebrow text-white" style={{ letterSpacing: "0.28em" }}>
              The Garage Gaming Store
            </span>
          </div>
          <p
            className="mt-6 max-w-xl text-base sm:text-lg"
            style={{ color: "var(--ab-text-muted)" }}
          >
            Trade-in quotes, live inventory pricing, and point of sale — all in one
            terminal-grade workspace for retro game retail.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/quote" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-quote">
              Get a Quote <ArrowRight size={16} />
            </Link>
            <Link to="/inventory-lookup" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-lookup">
              Shop Inventory
            </Link>
          </div>
        </div>
      </section>

      {/* Tiles */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24 -mt-14 relative z-10">
        <div className="grid md:grid-cols-3 gap-4">
          {TILES.map((t, i) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className="group surface p-6 sm:p-8 fade-in flex flex-col gap-5 hover:bg-[var(--ab-surface-2)] transition-colors"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="flex items-center justify-between">
                  <span className="label-eyebrow">{t.eyebrow}</span>
                  <Icon size={26} strokeWidth={1.5} style={{ color: "var(--ab-primary)" }} />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
                  {t.title}
                </h3>
                <p style={{ color: "var(--ab-text-muted)" }} className="text-sm leading-relaxed">
                  {t.desc}
                </p>
                <div className="mt-auto pt-4 flex items-center gap-2 label-eyebrow group-hover:text-[var(--ab-primary)] transition-colors">
                  Enter <ArrowRight size={14} />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* This Week's Bonus µPoint Promo */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24" data-testid="promo-section">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="label-eyebrow mb-2 inline-flex items-center gap-2" style={{ color: "var(--ab-primary)" }}>
              <Sparkles size={12} /> AB Members
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">This Week&apos;s Bonus µPoint Picks</h2>
            <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--ab-text-muted)" }}>
              Buy any of the titles below and earn an extra <b style={{ color: "var(--ab-primary)" }}>{(promo?.bonus_points || 1000).toLocaleString()} µPoints</b> on top of your regular earn rate. Members only — rotates every Wednesday.
            </p>
          </div>
          <Link to="/inventory-lookup" className="label-eyebrow inline-flex items-center gap-1 hover:text-[var(--ab-primary)] transition-colors" data-testid="promo-view-all">
            Shop Inventory <ArrowRight size={12} />
          </Link>
        </div>

        {promoLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="surface p-5 animate-pulse" style={{ minHeight: 120 }} />
            ))}
          </div>
        ) : !promo || !promo.items || promo.items.length === 0 ? (
          <div className="surface p-8 text-sm" style={{ color: "var(--ab-text-muted)" }} data-testid="promo-empty">
            New promo titles drop every Wednesday — check back soon.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {promo.items.map((it, i) => (
              <Link
                key={`${it.title}-${i}`}
                to="/inventory-lookup"
                data-testid="promo-item-card"
                className="surface p-5 flex flex-col gap-3 fade-in hover:bg-[var(--ab-surface-2)] transition-colors relative"
                style={{ animationDelay: `${i * 0.04}s`, borderLeft: "3px solid var(--ab-primary)" }}
              >
                <span className="absolute top-3 right-3 font-mono text-[10px] px-1.5 py-0.5 font-bold" style={{ background: "var(--ab-primary)", color: "#000" }} data-testid="promo-bonus-badge">
                  +{(promo.bonus_points || 1000).toLocaleString()} µPts
                </span>
                <div className="label-eyebrow truncate" style={{ maxWidth: "70%" }}>{it.console}</div>
                <div className="font-display text-lg font-bold leading-tight line-clamp-2" title={it.title}>
                  {it.title}
                </div>
                <div className="flex items-end justify-end mt-auto pt-1">
                  <span className="font-mono font-bold text-2xl" style={{ color: "var(--ab-primary)" }}>
                    ${Number(it.sale_price).toFixed(0)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
